import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SquaresFour,
  Users,
  Sparkle,
  FileText,
  Folder,
  SignOut,
  Target,
  Bell,
  CaretDown,
  ChatCircle,
  ShieldCheck,
} from '@phosphor-icons/react';
import { useHaptic } from '../../../hooks/useHaptic';
import { useNotifications } from '../../../contexts/NotificationContext';

const BASE_NAV_ITEMS = [
  { name: 'Dashboard',        path: '/',                  icon: SquaresFour,     exact: true },
  { 
    name: 'Alumni Directory', 
    path: '/alumni',            
    icon: Users,           
    exact: false,
    subItems: [
      { name: 'Directory', path: '/alumni' },
      { name: 'Payments', path: '/alumni/payments' }
    ]
  },
  { name: 'Proposals',        path: '/proposals',         icon: FileText,        exact: false },
  { name: 'Projects',         path: '/projects',          icon: Folder,          exact: false },
  { name: 'Proposal Matcher', path: '/proposal-matcher', icon: Sparkle,         exact: false },
  { name: 'Donor Predictor',  path: '/donor-predictor',  icon: Target,      exact: false },
  { name: 'Chat',             path: '/chat',             icon: ChatCircle,  exact: false },
];

export interface SidebarProps {
  onLogout: () => void;
}

export function Sidebar({ onLogout }: SidebarProps) {
  const { lightTap } = useHaptic();
  const location = useLocation();
  const navigate = useNavigate();
  const { togglePanel, tasks } = useNotifications();

  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(
    location.pathname.startsWith('/alumni') ? 'Alumni Directory' : null
  );

  const activeTasksCount = tasks.filter(
    (t) => t.status === 'processing' || t.status === 'completed'
  ).length;

  const role = localStorage.getItem('role') || 'user';
  const navItems = role === 'admin'
    // AI Settings is deliberately absent here — it is reached from the bottom of a project's
    // drawer, so it sits with the projects it configures rather than in the admin nav.
    ? [...BASE_NAV_ITEMS,
       { name: 'User Management', path: '/admin/users', icon: ShieldCheck, exact: false }]
    : BASE_NAV_ITEMS;

  const isActive = (item: any) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname === item.path ||
        (item.path !== '/' && location.pathname.startsWith(item.path));

  const userName = localStorage.getItem('name') || 'User';
  const initials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const glassStyle = {
    background: 'rgba(255, 255, 255, 0.92)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.06)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
  };

  return (
    <aside className="hidden md:flex flex-col fixed top-4 left-4 bottom-4 z-40 items-start justify-between pointer-events-none w-[220px]">
      {/* ── Top Pill: Profile ── */}
      <div 
        className="flex items-center justify-center w-[60px] py-3 rounded-full shrink-0 pointer-events-auto"
        style={glassStyle}
      >
        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center shadow-sm">
          <span className="text-white font-black text-sm font-outfit leading-none select-none">{initials}</span>
        </div>
      </div>

      {/* ── Middle Pill: Main Nav (Expanding) ── */}
      <nav 
        onMouseEnter={() => setIsNavExpanded(true)}
        onMouseLeave={() => setIsNavExpanded(false)}
        className="flex flex-col gap-2.5 transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] py-4 px-2 rounded-[30px] shrink-0 pointer-events-auto overflow-hidden hide-scrollbar max-h-full overflow-y-auto"
        style={{
          ...glassStyle,
          width: isNavExpanded ? '220px' : '60px'
        }}
      >
        {navItems.map((item) => {
          const active = isActive(item);
          const hasSubItems = !!item.subItems?.length;
          const isOpen = openAccordion === item.name;
          const Icon = item.icon;

          return (
            <div key={item.name} className="w-[202px] shrink-0">
              <button
                onClick={() => {
                  lightTap();
                  if (hasSubItems) {
                    setOpenAccordion(isOpen ? null : item.name);
                  } else {
                    navigate(item.path);
                  }
                }}
                title={!isNavExpanded ? item.name : undefined}
                aria-label={item.name}
                className="relative w-full h-[44px] transition-all duration-300 rounded-full flex items-center focus:outline-none cursor-pointer group"
              >
                {/* Animated active background */}
                {active && !hasSubItems && (
                  <motion.div
                    layoutId="sidebar-active-bg"
                    className="absolute left-0 top-0 w-[44px] h-[44px] rounded-full bg-accent"
                    transition={{ type: 'spring', stiffness: 420, damping: 36 }}
                  />
                )}

                {/* Icon (fixed at 44px wide to perfectly center in the 60px pill minus 8px padding) */}
                <div className="w-[44px] h-[44px] shrink-0 flex items-center justify-center relative z-10 transition-transform duration-300 group-hover:scale-110">
                  <Icon
                    size={22}
                    weight={active && !hasSubItems ? 'fill' : 'regular'}
                    className={`transition-colors ${
                      active && !hasSubItems
                        ? 'text-white'
                        : 'text-slate-400 group-hover:text-slate-700'
                    }`}
                  />
                </div>

                {/* Label + Caret (only visible when nav is hovered) */}
                <div className={`ml-1 flex items-center justify-between flex-1 overflow-hidden transition-opacity duration-200 whitespace-nowrap pr-3 ${isNavExpanded ? 'opacity-100 delay-100' : 'opacity-0'}`}>
                  <span className={`text-sm font-semibold truncate ${active && !hasSubItems ? 'text-slate-900' : 'text-slate-600'}`}>
                    {item.name}
                  </span>
                  {hasSubItems && (
                    <CaretDown
                      size={14}
                      weight="bold"
                      className={`ml-2 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${active ? 'text-slate-900' : 'text-slate-400'}`}
                    />
                  )}
                </div>
              </button>

              {/* Sub-items */}
              {hasSubItems && (
                <AnimatePresence initial={false}>
                  {isOpen && isNavExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.16 }}
                      className="overflow-hidden w-full"
                    >
                      <div className="ml-[22px] mt-1 pl-3 border-l-2 border-slate-100 flex flex-col gap-1 py-1">
                        {item.subItems?.map((sub) => {
                          const subActive = location.pathname === sub.path;
                          return (
                            <button
                              key={sub.name}
                              onClick={() => { lightTap(); navigate(sub.path); }}
                              className={`h-8 w-full text-left px-3 text-xs rounded-lg transition-smooth cursor-pointer whitespace-nowrap ${
                                subActive
                                  ? 'text-slate-900 font-bold bg-slate-100/80'
                                  : 'text-slate-500 font-medium hover:text-slate-900 hover:bg-slate-50'
                              }`}
                            >
                              {sub.name}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          );
        })}
      </nav>

      {/* ── Bottom Pill: Notifications + Logout ── */}
      <div 
        className="flex flex-col items-center gap-1.5 w-[60px] py-3 px-2 rounded-[30px] shrink-0 pointer-events-auto group/bottom"
        style={glassStyle}
      >
        {/* Notifications */}
        <button
          onClick={() => { lightTap(); togglePanel(); }}
          title="Notifications"
          aria-label="Notifications"
          className="relative w-[44px] h-[44px] rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-smooth cursor-pointer group hover:scale-110"
        >
          <Bell size={22} weight="regular" />
          {activeTasksCount > 0 && (
            <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-accent border-2 border-white" />
          )}
          <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 bg-slate-900 text-white text-[11px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50">
            Notifications
          </span>
        </button>

        {/* Logout */}
        <button
          onClick={() => { lightTap(); onLogout(); }}
          title="Logout"
          aria-label="Logout"
          className="relative w-[44px] h-[44px] rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-smooth cursor-pointer group hover:scale-110"
        >
          <SignOut size={22} weight="regular" />
          <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 bg-slate-900 text-white text-[11px] font-semibold rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg z-50">
            Logout
          </span>
        </button>
      </div>
    </aside>
  );
}
